import React, { useEffect } from "react"
import { motion } from "framer-motion"
import { Route, Switch } from "react-router"
import { useDispatch, useSelector } from "react-redux"
import MemberTopBar from "../Components/MemberTopBar"
import MemberPortal from "../Pages/Users/MemberPortal"
import MemberMatches from "../Pages/Users/MemberMatches"
import Chat from "../Pages/Users/Chat"
import MemberPlans from "../Pages/Users/MemberPlans"
import { LanguageProvider } from "../Common/LanguageContext"
import API from "../Common/API"
import { setUserDetails, setAllStars, setZodiacSigns, setFlaws, setStatus, setOccupation, setDhisa } from "../Store/Auth/actions"

const MemberHomeRoot = () => {
    const dispatch = useDispatch()
    const userDetails = useSelector((state) => state.auth.userDetails)

    // Fetch full profile on mount so plan fields (isPaid, planValidityDate, planId)
    // are available — the JWT token only carries basic identity fields.
    useEffect(() => {
        if (!userDetails?._id) return
        API.get(`/user/getUserDetailsById/${userDetails._id}`)
            .then((res) => {
                if (res.status === 200 && res.data?.data) {
                    const full = res.data.data
                    const merged = { ...userDetails, ...full }
                    dispatch(setUserDetails(merged))
                    // Persist without images to stay under the 5 MB localStorage cap
                    const { images: _stripped, ...safe } = merged
                    try { localStorage.setItem("userDetails", JSON.stringify(safe)) } catch { /* quota */ }
                }
            })
            .catch(() => {})
    }, [userDetails?._id]) // eslint-disable-line

    // Load all master data needed for the member portal (zodiac signs, stars, flaws, etc.)
    useEffect(() => {
        const CACHE_KEY = 'matrimony_master_data_v2'
        const CACHE_TTL = 6 * 60 * 60 * 1000

        const applyData = (d) => {
            if (!d) return
            const stars    = d.stars    || []
            const zodiacs  = d.zodiacSigns || []
            const flaws    = d.flaws    || []
            const status   = d.statusList || []
            const occ      = d.occupations || []
            const dhisa    = d.dhisa    || []
            if (stars.length)   dispatch(setAllStars(stars.map(s => ({ text: s.starName || s.star, value: s._id }))))
            if (zodiacs.length) dispatch(setZodiacSigns(zodiacs.map(z => ({ text: z.zodiacSign, value: z._id }))))
            if (flaws.length)   dispatch(setFlaws(flaws.map(f => ({ text: f.flawName || f.flaw || f.name || f._id, value: f._id }))))
            if (status.length)  dispatch(setStatus(status.map(s => ({ text: s.status, value: s._id }))))
            if (occ.length)     dispatch(setOccupation(occ.map(o => ({ text: o.occupation, value: o._id }))))
            if (dhisa.length)   dispatch(setDhisa(dhisa.map(d => ({ text: d.dhisa, value: d.dhisaId }))))
        }

        // Try sessionStorage cache first (shared with admin Root.js)
        try {
            const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null')
            if (cached?.timestamp && Date.now() - cached.timestamp < CACHE_TTL && cached.data) {
                applyData(cached.data)
                return  // cache hit — no network call needed
            }
        } catch { /* ignore */ }

        // Cache miss — fetch all master data
        Promise.allSettled([
            API.get('/common/getAllStars'),
            API.get('/common/getZodiacSign'),
            API.get('/common/getAllFlaws'),
            API.get('/common/getStatusList'),
            API.get('/common/getOccupations'),
            API.get('/common/getDhisa'),
        ]).then(([starRes, zodiacRes, flawRes, statRes, occuRes, dhisaRes]) => {
            const safe = (r) => (r.status === 'fulfilled' ? r.value?.data?.data || [] : [])
            const data = {
                stars:       safe(starRes),
                zodiacSigns: safe(zodiacRes),
                flaws:       safe(flawRes),
                statusList:  safe(statRes),
                occupations: safe(occuRes),
                dhisa:       safe(dhisaRes),
            }
            try { sessionStorage.setItem(CACHE_KEY, JSON.stringify({ timestamp: Date.now(), data })) } catch { /* quota */ }
            applyData(data)
        }).catch(() => {})
    }, []) // eslint-disable-line

    return (
        <LanguageProvider>
            <motion.div className="min-h-screen w-full bg-pColor">
                <MemberTopBar />
                <div className="mx-auto max-w-[1700px] px-4 py-5 md:px-10 md:py-8">
                    <Switch>
                        <Route path="/member" exact component={MemberMatches} />
                        <Route path="/member/profile" exact component={MemberPortal} />
                        <Route path="/member/matches" exact component={MemberMatches} />
                        <Route path="/member/chat" exact component={Chat} />
                        <Route path="/member/chat/:userId" exact component={Chat} />
                        <Route path="/member/plans" exact component={MemberPlans} />
                    </Switch>
                </div>
            </motion.div>
        </LanguageProvider>
    )
}

export default MemberHomeRoot
